<template>
  <div class="plinko-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-plinko" :size="18" /> {{ $t('games.plinko.title') }}
        <span class="game-tag">{{ $t('games.plinko.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ active: soundEnabled }"
          :title="$t('games.toggle_sound')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas Area -->
      <div class="game-canvas-area" ref="canvasContainer">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <div class="plinko-canvas-wrap">
          <!-- PixiJS mounts here -->
          <div ref="pixiMount" class="pixi-mount"></div>

          <!-- Multiplier display row (below canvas, color-coded) -->
          <div class="plinko-mult-row">
            <div
              v-for="(mult, i) in currentMultipliers"
              :key="i"
              class="mult-chip"
              :class="multChipClass(mult)"
            >{{ formatMult(mult) }}</div>
          </div>

          <!-- Result pills strip (last 20 results) -->
          <div class="plinko-results-strip">
            <div
              v-for="(r, i) in displayResults"
              :key="i"
              class="plinko-result-pill"
              :class="pillClass(r.multiplier)"
            >
              {{ r.multiplier.toFixed(1) }}×
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Game Sidebar -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >{{ $t('games.bet_tab') }}</button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'auto' }"
            @click="activeTab = 'auto'"
          >{{ $t('games.auto_tab') }}</button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'rules' }"
            @click="activeTab = 'rules'"
          >{{ $t('games.rules_tab') }}</button>
        </div>

        <!-- BET TAB -->
        <div class="game-tab-content" :class="{ active: activeTab === 'bet' }">
          <label class="ctrl-label">{{ $t('games.bet_amount') }}</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              class="bet-input"
              type="number"
              min="0.01"
              step="0.01"
              v-model.number="betAmount"
              :disabled="loading || isAutoBet"
            />
          </div>
          <div class="bet-shortcuts">
            <button class="bet-short" @click="halveBet" :disabled="loading || isAutoBet">½</button>
            <button class="bet-short" @click="doubleBet" :disabled="loading || isAutoBet">2×</button>
            <button class="bet-short" @click="maxBet" :disabled="loading || isAutoBet">Max</button>
          </div>

          <!-- Row selector -->
          <label class="ctrl-label">{{ $t('games.plinko.rows') }}</label>
          <div class="option-group">
            <button
              v-for="r in SUPPORTED_ROWS"
              :key="r"
              class="option-btn"
              :class="{ active: rows === r }"
              :disabled="loading || isAutoBet"
              @click="setRows(r)"
            >{{ r }}</button>
          </div>

          <!-- Risk selector -->
          <label class="ctrl-label" style="margin-top: 10px">{{ $t('games.plinko.risk') }}</label>
          <div class="option-group">
            <button
              v-for="lv in SUPPORTED_RISKS"
              :key="lv"
              class="option-btn"
              :class="{ active: risk === lv }"
              :disabled="loading || isAutoBet"
              @click="setRisk(lv)"
            >{{ riskLabel(lv) }}</button>
          </div>

          <!-- Stats -->
          <div class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.plinko.balls_active') }}</div>
              <div class="sp-value">{{ ballsInFlight }}</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.plinko.last_multiplier') }}</div>
              <div class="sp-value gold">
                {{ lastMultiplier !== null ? lastMultiplier.toFixed(2) + '×' : '—' }}
              </div>
            </div>
          </div>

          <button
            class="btn-action"
            :disabled="loading || isAutoBet || betAmount <= 0"
            @click="placeBet"
          >
            {{ loading && !isAutoBet ? $t('games.plinko.dropping') : $t('games.place_bet') }}
          </button>

          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>

        <!-- AUTO TAB -->
        <div class="game-tab-content" :class="{ active: activeTab === 'auto' }">
          <label class="ctrl-label">{{ $t('games.number_of_bets') }}</label>
          <div class="auto-field">
            <input
              class="auto-input"
              type="number"
              v-model.number="autoDrops"
              min="1"
              max="1000"
              :disabled="isAutoBet"
              placeholder="10"
            />
          </div>

          <label class="ctrl-label">{{ $t('games.on_win') }}</label>
          <div class="auto-field">
            <select class="auto-select" v-model="onWinAction" :disabled="isAutoBet">
              <option value="reset">{{ $t('games.reset') }}</option>
              <option value="increase">{{ $t('games.increase_by') }}</option>
            </select>
          </div>
          <div v-if="onWinAction === 'increase'" class="auto-field">
            <div class="bet-input-wrap" style="margin-bottom: 0">
              <input
                class="auto-input"
                style="padding-left: 10px"
                type="number"
                v-model.number="onWinPercent"
                min="0"
                max="100"
                step="0.5"
                :disabled="isAutoBet"
                placeholder="e.g. 2"
              />
              <span class="auto-suffix">%</span>
            </div>
          </div>

          <label class="ctrl-label">{{ $t('games.on_loss') }}</label>
          <div class="auto-field">
            <select class="auto-select" v-model="onLossAction" :disabled="isAutoBet">
              <option value="reset">{{ $t('games.reset') }}</option>
              <option value="increase">{{ $t('games.increase_by') }}</option>
            </select>
          </div>
          <div v-if="onLossAction === 'increase'" class="auto-field">
            <div class="bet-input-wrap" style="margin-bottom: 0">
              <input
                class="auto-input"
                style="padding-left: 10px"
                type="number"
                v-model.number="onLossPercent"
                min="0"
                max="200"
                step="0.5"
                :disabled="isAutoBet"
                placeholder="e.g. 100"
              />
              <span class="auto-suffix">%</span>
            </div>
          </div>

          <div class="ctrl-divider"></div>

          <label class="ctrl-label">{{ $t('games.stop_on_profit') }}</label>
          <div class="auto-field">
            <div class="bet-input-wrap" style="margin-bottom: 0">
              <span class="bet-currency">$</span>
              <input
                class="auto-input"
                style="padding-left: 24px"
                type="number"
                v-model.number="stopOnProfit"
                min="0"
                step="0.01"
                :disabled="isAutoBet"
                placeholder="0.00 (disabled)"
              />
            </div>
          </div>

          <label class="ctrl-label">{{ $t('games.stop_on_loss') }}</label>
          <div class="auto-field">
            <div class="bet-input-wrap" style="margin-bottom: 0">
              <span class="bet-currency">$</span>
              <input
                class="auto-input"
                style="padding-left: 24px"
                type="number"
                v-model.number="stopOnLoss"
                min="0"
                step="0.01"
                :disabled="isAutoBet"
                placeholder="0.00 (disabled)"
              />
            </div>
          </div>

          <!-- Progress while running -->
          <div v-if="isAutoBet" class="auto-progress">
            <div class="auto-progress-bar">
              <div class="auto-progress-fill" :style="{ width: autoProgressPct + '%' }"></div>
            </div>
            <span class="auto-progress-label">
              {{ autoDropsDone }} / {{ autoDrops }} drops —
              <span :class="autoSessionProfit >= 0 ? 'auto-net-pos' : 'auto-net-neg'">
                {{ autoSessionProfit >= 0 ? '+' : '' }}${{ Math.abs(autoSessionProfit).toFixed(2) }}
              </span>
            </span>
          </div>

          <button
            class="btn-action"
            :class="{ 'btn-auto-stop': isAutoBet }"
            :disabled="!isAutoBet && (betAmount <= 0 || autoDrops <= 0)"
            style="margin-top: 14px"
            @click="isAutoBet ? stopAutoBet() : startAutoBet()"
          >
            {{ isAutoBet ? $t('games.stop_auto') : $t('games.start_auto_bet') }}
          </button>

          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>

        <!-- RULES TAB -->
        <div class="game-tab-content" :class="{ active: activeTab === 'rules' }">
          <div class="rules-section">
            <div class="rules-title"><span class="rules-title-icon"><AppIcon name="game-plinko" :size="18" /></span> How to Play</div>
            <ul class="rules-list">
              <li class="rules-item">Choose the number of rows (8, 12, 14 or 16) to set game complexity</li>
              <li class="rules-item">Select your risk level: Low, Medium, or High</li>
              <li class="rules-item">Place your bet and watch the ball drop through the pegs</li>
              <li class="rules-item">The ball lands in one of the buckets at the bottom, determining your multiplier</li>
              <li class="rules-item">Higher risk means bigger potential wins but also bigger losses</li>
              <li class="rules-item">All outcomes are provably fair and server-determined</li>
            </ul>
            <div class="rules-note">
              The ball path is determined by your provably fair hash before the drop begins.
            </div>
          </div>
          <div class="fair-tag"><AppIcon name="provably-fair" :size="12" /> Provably Fair Game</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Application, Graphics, Container, Text, TextStyle } from 'pixi.js'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toastStore = useToastStore()

// ----- Constants -----
const SUPPORTED_ROWS = [8, 12, 14, 16] as const
const SUPPORTED_RISKS = ['low', 'medium', 'high'] as const

/** Mirror of PlinkoGame::DEFAULT_MULTIPLIERS */
const MULTIPLIERS: Record<string, Record<number, number[]>> = {
  low: {
    8:  [5.6, 2.1, 1.1, 1.0, 0.5, 1.0, 1.1, 2.1, 5.6],
    12: [10.0, 3.0, 1.6, 1.4, 1.1, 1.0, 0.5, 1.0, 1.1, 1.4, 1.6, 3.0, 10.0],
    14: [7.1, 4.0, 1.9, 1.4, 1.3, 1.1, 1.0, 0.5, 1.0, 1.1, 1.3, 1.4, 1.9, 4.0, 7.1],
    16: [16.0, 9.0, 2.0, 1.4, 1.4, 1.2, 1.1, 1.0, 0.5, 1.0, 1.1, 1.2, 1.4, 1.4, 2.0, 9.0, 16.0],
  },
  medium: {
    8:  [13.0, 3.0, 1.3, 0.7, 0.4, 0.7, 1.3, 3.0, 13.0],
    12: [33.0, 11.0, 4.0, 2.0, 1.1, 0.6, 0.3, 0.6, 1.1, 2.0, 4.0, 11.0, 33.0],
    14: [559.0, 30.0, 20.0, 3.0, 1.3, 0.6, 0.4, 0.3, 0.4, 0.6, 1.3, 3.0, 20.0, 30.0, 559.0],
    16: [110.0, 41.0, 10.0, 5.0, 3.0, 1.5, 1.0, 0.5, 0.3, 0.5, 1.0, 1.5, 3.0, 5.0, 10.0, 41.0, 110.0],
  },
  high: {
    8:  [29.0, 4.0, 1.5, 0.3, 0.2, 0.3, 1.5, 4.0, 29.0],
    12: [170.0, 24.0, 8.1, 2.0, 0.7, 0.2, 0.2, 0.2, 0.7, 2.0, 8.1, 24.0, 170.0],
    14: [839.0, 50.0, 20.0, 4.4, 1.4, 0.4, 0.2, 0.2, 0.2, 0.4, 1.4, 4.4, 20.0, 50.0, 839.0],
    16: [1000.0, 130.0, 26.0, 9.0, 4.0, 2.0, 0.2, 0.2, 0.2, 0.2, 0.2, 2.0, 4.0, 9.0, 26.0, 130.0, 1000.0],
  },
}

// ----- State -----
const rows = ref<8 | 12 | 14 | 16>(8)
const risk = ref<'low' | 'medium' | 'high'>('low')
const betAmount = ref(1.00)
const loading = ref(false)
const soundEnabled = ref(false)
const activeTab = ref<'bet' | 'auto' | 'rules'>('bet')
const ballsInFlight = ref(0)
const lastMultiplier = ref<number | null>(null)
const results = ref<Array<{ multiplier: number; isWin: boolean }>>([])

// ----- Auto-bet state -----
const isAutoBet = ref(false)
const autoDrops = ref(10)
const autoDropsDone = ref(0)
const autoSessionProfit = ref(0)
const stopOnProfit = ref<number | null>(null)
const stopOnLoss = ref<number | null>(null)
const onWinAction = ref<'reset' | 'increase'>('reset')
const onWinPercent = ref(2)
const onLossAction = ref<'reset' | 'increase'>('reset')
const onLossPercent = ref(100)
const baseBetForAuto = ref(1.00)
let autoBetAborted = false

// Show last 20 results (newest last)
const displayResults = computed(() => results.value.slice(-20))

// Multiplier chips for current rows/risk
const currentMultipliers = computed<number[]>(() => MULTIPLIERS[risk.value][rows.value] ?? [])

// Auto-bet progress
const autoProgressPct = computed(() =>
  autoDrops.value > 0 ? Math.min(100, (autoDropsDone.value / autoDrops.value) * 100) : 0
)

// ----- PixiJS refs -----
const pixiMount = ref<HTMLDivElement | null>(null)
const canvasContainer = ref<HTMLDivElement | null>(null)
let app: Application | null = null
let boardContainer: Container | null = null
let ballGraphics: Graphics | null = null
let highlightGraphics: Graphics | null = null
let resizeObserver: ResizeObserver | null = null

// Animation state
interface AnimState {
  active: boolean
  waypoints: Array<{ x: number; y: number }>
  elapsed: number       // ms elapsed
  duration: number      // total ms for full animation
  landingBucket: number
  highlightAlpha: number
  onComplete: () => void
}
let animState: AnimState | null = null

// ----- Wallet / balance -----
const formattedBalance = computed<string>(() => {
  const b = walletStore.balance?.balance ?? 0
  return '$' + (b / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
})

// ----- API types -----
interface PlayResponse {
  data: {
    payout: number
    multiplier: number
    is_win: boolean
    outcome_data: { path: number[] }
    game_round_id: string
    balance: { bonus_used: number; cash_used: number }
  }
}

// ----- Controls -----
function halveBet() {
  betAmount.value = Math.max(0.01, betAmount.value / 2)
}
function doubleBet() {
  betAmount.value = betAmount.value * 2
}
function maxBet() {
  const bal = walletStore.balance?.balance ?? 0
  betAmount.value = Math.max(0.01, bal / 100)
}
function setRows(r: 8 | 12 | 14 | 16) {
  rows.value = r
  redrawBoard()
}
function setRisk(lv: 'low' | 'medium' | 'high') {
  risk.value = lv
  redrawBoard()
}
function riskLabel(lv: string): string {
  return lv === 'low' ? 'Low' : lv === 'medium' ? 'Med' : 'High'
}
function pillClass(mult: number): string {
  if (mult >= 5) return 'big'
  if (mult >= 1) return 'mid'
  return 'low'
}
function multChipClass(mult: number): string {
  if (mult >= 5) return 'chip-high'
  if (mult >= 1) return 'chip-mid'
  return 'chip-low'
}
function formatMult(mult: number): string {
  if (mult >= 100) return mult.toFixed(0) + '×'
  return mult.toFixed(1) + '×'
}

// ----- PixiJS init -----
function initPixi() {
  const mount = pixiMount.value
  if (!mount) return

  const w = mount.clientWidth || 380
  const h = mount.clientHeight || 460

  app = new Application({
    width: w,
    height: h,
    backgroundColor: 0x0D1B2A,
    antialias: true,
    resolution: window.devicePixelRatio || 1,
    autoDensity: true,
  })

  mount.appendChild(app.view as HTMLCanvasElement)

  // Create scene layers
  boardContainer = new Container()
  app.stage.addChild(boardContainer)

  ballGraphics = new Graphics()
  app.stage.addChild(ballGraphics)

  highlightGraphics = new Graphics()
  app.stage.addChild(highlightGraphics)

  drawBoard()

  // Animation ticker
  app.ticker.add(onTick)

  // Responsive resize
  resizeObserver = new ResizeObserver(() => {
    if (!app || !mount) return
    const newW = mount.clientWidth
    const newH = mount.clientHeight
    if (newW > 0 && newH > 0) {
      app.renderer.resize(newW, newH)
      drawBoard()
    }
  })
  resizeObserver.observe(mount)
}

// ----- Board drawing -----
function getLayout(w: number, h: number, numRows: number) {
  const pegRadius = Math.max(4, Math.min(8, w / (numRows * 8)))
  const rowSpacing = (h * 0.65) / numRows
  const topMargin = h * 0.10
  const centerX = w / 2
  const bucketY = h * 0.80
  const bucketHeight = h * 0.13
  const bucketCount = numRows + 1
  const bucketWidth = w / bucketCount
  return { pegRadius, rowSpacing, topMargin, centerX, bucketY, bucketHeight, bucketCount, bucketWidth }
}

function drawBoard() {
  if (!app || !boardContainer) return

  boardContainer.removeChildren()

  const w = app.renderer.width / (app.renderer.resolution || 1)
  const h = app.renderer.height / (app.renderer.resolution || 1)
  const numRows = rows.value
  const { pegRadius, rowSpacing, topMargin, centerX, bucketY, bucketHeight, bucketCount, bucketWidth } =
    getLayout(w, h, numRows)

  const mults = MULTIPLIERS[risk.value][numRows]

  // --- Draw pegs ---
  const pegGfx = new Graphics()
  pegGfx.beginFill(0x00E701, 0.45)
  pegGfx.lineStyle(1, 0x00E701, 0.7)

  for (let row = 0; row < numRows; row++) {
    const pegsInRow = row + 2
    const y = topMargin + row * rowSpacing
    const xStart = centerX - ((pegsInRow - 1) * rowSpacing) / 2

    for (let col = 0; col < pegsInRow; col++) {
      const x = xStart + col * rowSpacing
      pegGfx.drawCircle(x, y, pegRadius)
    }
  }

  pegGfx.endFill()
  boardContainer.addChild(pegGfx)

  // --- Draw buckets ---
  const bucketContainer = new Container()
  const fontSize = Math.max(9, Math.min(13, w / (bucketCount * 4.5)))

  for (let i = 0; i < bucketCount; i++) {
    const mult = mults[i] ?? 0
    const bx = i * bucketWidth
    const color = mult >= 5 ? 0x00E701 : mult >= 1 ? 0xFFD700 : 0x1475E1
    const alpha = 0.7

    const gfx = new Graphics()
    gfx.beginFill(color, alpha)
    gfx.drawRect(bx + 2, bucketY, bucketWidth - 4, bucketHeight)
    gfx.endFill()
    bucketContainer.addChild(gfx)

    // Label
    const label = mult >= 100
      ? mult.toFixed(0) + '×'
      : mult.toFixed(1) + '×'

    const textStyle = new TextStyle({
      fontFamily: 'JetBrains Mono, monospace',
      fontSize,
      fontWeight: 'bold',
      fill: '#ffffff',
    })
    const text = new Text(label, textStyle)
    text.anchor.set(0.5, 0.5)
    text.x = bx + bucketWidth / 2
    text.y = bucketY + bucketHeight / 2
    bucketContainer.addChild(text)
  }

  boardContainer.addChild(bucketContainer)
}

// ----- Waypoint building -----
function buildWaypoints(
  path: number[],
  numRows: number,
  w: number,
  h: number
): Array<{ x: number; y: number }> {
  const { rowSpacing, topMargin, centerX, bucketY, bucketHeight, bucketCount, bucketWidth } =
    getLayout(w, h, numRows)

  const wps: Array<{ x: number; y: number }> = []

  // Start position: above the first peg row
  wps.push({ x: centerX, y: topMargin - rowSpacing * 0.5 })

  let ballX = centerX

  // After each peg row: ball shifts left (0) or right (1) by rowSpacing/2
  for (let i = 0; i < numRows; i++) {
    const dir = path[i] === 1 ? 1 : -1
    ballX += dir * (rowSpacing / 2)
    // Waypoint is between this row and the next (or at bucket)
    const wpY = topMargin + i * rowSpacing + rowSpacing * 0.65
    wps.push({ x: ballX, y: wpY })
  }

  // Final: bucket center
  const bucket = path.filter(b => b === 1).length
  const safeI = Math.min(Math.max(bucket, 0), bucketCount - 1)
  wps.push({
    x: safeI * bucketWidth + bucketWidth / 2,
    y: bucketY + bucketHeight / 2,
  })

  return wps
}

// ----- Animation ticker -----
function onTick(deltaMs: number) {
  if (!animState || !animState.active || !ballGraphics || !highlightGraphics || !app) return

  animState.elapsed += deltaMs * (1000 / 60) // convert PixiJS delta to ms

  const { waypoints, duration, elapsed, landingBucket, onComplete } = animState
  const rawProgress = Math.min(elapsed / duration, 1.0)

  // Ease: cubic in-out for per-segment smooth motion
  const segCount = waypoints.length - 1
  const segProgress = rawProgress * segCount
  const segIndex = Math.min(Math.floor(segProgress), segCount - 1)
  const t = segProgress - segIndex
  const eased = easeInOutCubic(t)

  const from = waypoints[segIndex]
  const to = waypoints[segIndex + 1]
  const bx = lerp(from.x, to.x, eased)
  const by = lerp(from.y, to.y, eased)

  // Draw ball
  const w = app.renderer.width / (app.renderer.resolution || 1)
  const h = app.renderer.height / (app.renderer.resolution || 1)
  const numRows = rows.value
  const { pegRadius } = getLayout(w, h, numRows)
  const ballRadius = pegRadius * 1.6

  ballGraphics.clear()
  // Glow
  ballGraphics.beginFill(0xFFD700, 0.25)
  ballGraphics.drawCircle(bx, by, ballRadius * 2.2)
  ballGraphics.endFill()
  // Ball
  ballGraphics.beginFill(0xFFD700)
  ballGraphics.drawCircle(bx, by, ballRadius)
  ballGraphics.endFill()

  // Bucket highlight (phase in at the end)
  if (rawProgress > 0.85) {
    const alpha = (rawProgress - 0.85) / 0.15
    animState.highlightAlpha = alpha
    const { bucketY, bucketHeight, bucketWidth } = getLayout(w, h, numRows)
    const safeI = Math.min(Math.max(landingBucket, 0), numRows)
    highlightGraphics.clear()
    highlightGraphics.beginFill(0xFFFFFF, alpha * 0.35)
    highlightGraphics.drawRect(
      safeI * bucketWidth + 2,
      bucketY,
      bucketWidth - 4,
      bucketHeight
    )
    highlightGraphics.endFill()
  }

  if (rawProgress >= 1.0) {
    animState.active = false
    // Fade out highlight after 1 second
    setTimeout(() => {
      if (highlightGraphics) highlightGraphics.clear()
    }, 1000)
    onComplete()
  }
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

// ----- Core drop function (shared by manual bet and auto-bet) -----
interface DropResult {
  multiplier: number
  isWin: boolean
  payout: number
  betUsed: number
}

async function runDrop(): Promise<DropResult> {
  const betUsed = betAmount.value

  loading.value = true
  ballsInFlight.value++

  try {
    const res = await apiClient.post<PlayResponse['data']>('/api/games/plinko/play', {
      amount: betUsed.toFixed(2),
      options: {
        rows: rows.value,
        risk: risk.value,
      },
    })

    const data = res.data
    const fullPath: number[] = data.outcome_data?.path ?? []
    const slicedPath = fullPath.slice(0, rows.value)
    const bucket = slicedPath.filter(b => b === 1).length
    const multiplier = Number(data.multiplier)
    const isWin = data.is_win
    const payout = data.payout

    // Update results history
    results.value.push({ multiplier, isWin })
    if (results.value.length > 50) results.value.shift()
    lastMultiplier.value = multiplier

    // Build animation waypoints and run animation
    if (app) {
      const w = app.renderer.width / (app.renderer.resolution || 1)
      const h = app.renderer.height / (app.renderer.resolution || 1)
      const waypoints = buildWaypoints(slicedPath, rows.value, w, h)

      // Auto-bet uses faster animation
      const totalDuration = isAutoBet.value
        ? rows.value * 70 + 200
        : rows.value * 120 + 400

      await new Promise<void>((resolve) => {
        animState = {
          active: true,
          waypoints,
          elapsed: 0,
          duration: totalDuration,
          landingBucket: bucket,
          highlightAlpha: 0,
          onComplete: () => {
            setTimeout(() => {
              if (ballGraphics) ballGraphics.clear()
            }, isAutoBet.value ? 300 : 1500)
            resolve()
          },
        }
      })
    }

    ballsInFlight.value = Math.max(0, ballsInFlight.value - 1)
    loading.value = false
    walletStore.applyGameResult(betUsed, payout)

    return { multiplier, isWin, payout, betUsed }
  } catch (err) {
    ballsInFlight.value = Math.max(0, ballsInFlight.value - 1)
    loading.value = false
    throw err
  }
}

// ----- Manual bet -----
async function placeBet() {
  if (loading.value || isAutoBet.value) return

  const bal = walletStore.balance?.balance ?? 0
  const betCents = Math.round(betAmount.value * 100)
  if (betCents <= 0) {
    toastStore.error(t('games.invalid_bet_amount'))
    return
  }
  if (betCents > bal) {
    toastStore.error(t('games.insufficient_balance'))
    return
  }

  try {
    const result = await runDrop()
    const profit = result.payout - result.betUsed
    if (result.isWin) {
      toastStore.success(`Won $${profit.toFixed(2)} — ${result.multiplier.toFixed(2)}×`)
    } else {
      toastStore.error(`Lost $${result.betUsed.toFixed(2)} — ${result.multiplier.toFixed(2)}×`)
    }
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    toastStore.error(msg ?? 'Bet failed. Please try again.')
  }
}

// ----- Auto-bet -----
async function startAutoBet() {
  if (betAmount.value <= 0 || autoDrops.value <= 0) {
    toastStore.error(t('games.invalid_autobet'))
    return
  }

  saveAutoSettings()

  isAutoBet.value = true
  autoBetAborted = false
  baseBetForAuto.value = betAmount.value
  autoDropsDone.value = 0
  autoSessionProfit.value = 0

  let dropsLeft = autoDrops.value

  while (dropsLeft > 0 && !autoBetAborted) {
    const bal = walletStore.balance?.balance ?? 0
    const betCents = Math.round(betAmount.value * 100)

    if (betCents <= 0 || betCents > bal) {
      toastStore.error(t('games.autobet_stopped_balance'))
      break
    }

    try {
      const result = await runDrop()

      if (autoBetAborted) break

      autoDropsDone.value++
      dropsLeft--
      autoSessionProfit.value = Math.round((autoSessionProfit.value + result.payout - result.betUsed) * 10000) / 10000

      // Apply on-win / on-loss strategy
      if (result.isWin) {
        if (onWinAction.value === 'increase' && onWinPercent.value > 0) {
          betAmount.value = Math.max(0.01, Math.round(betAmount.value * (1 + onWinPercent.value / 100) * 100) / 100)
        } else {
          betAmount.value = baseBetForAuto.value
        }
      } else {
        if (onLossAction.value === 'increase' && onLossPercent.value > 0) {
          betAmount.value = Math.max(0.01, Math.round(betAmount.value * (1 + onLossPercent.value / 100) * 100) / 100)
        } else {
          betAmount.value = baseBetForAuto.value
        }
      }

      // Check stop conditions
      if (stopOnProfit.value && stopOnProfit.value > 0 && autoSessionProfit.value >= stopOnProfit.value) {
        toastStore.success(t('games.autobet_stopped_profit', { amount: autoSessionProfit.value.toFixed(2) }))
        break
      }
      if (stopOnLoss.value && stopOnLoss.value > 0 && autoSessionProfit.value <= -stopOnLoss.value) {
        toastStore.error(t('games.autobet_stopped_loss', { amount: Math.abs(autoSessionProfit.value).toFixed(2) }))
        break
      }

      // Brief pause between drops
      if (dropsLeft > 0 && !autoBetAborted) {
        await new Promise(resolve => setTimeout(resolve, 80))
      }
    } catch (err: unknown) {
      if (!autoBetAborted) {
        const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        toastStore.error(msg ?? 'Auto-bet error')
      }
      break
    }
  }

  isAutoBet.value = false
  if (!autoBetAborted && autoDropsDone.value > 0) {
    const net = autoSessionProfit.value
    if (net > 0) {
      toastStore.success(`Auto-bet finished. Net: +$${net.toFixed(2)}`)
    } else if (net < 0) {
      toastStore.error(`Auto-bet finished. Net: -$${Math.abs(net).toFixed(2)}`)
    } else {
      toastStore.success('Auto-bet finished. Break even.')
    }
  }
  betAmount.value = baseBetForAuto.value
}

function stopAutoBet() {
  autoBetAborted = true
  isAutoBet.value = false
}

// ----- Session persistence for auto-bet settings -----
const AUTO_STORAGE_KEY = 'plinko_auto_settings'

function saveAutoSettings() {
  try {
    sessionStorage.setItem(AUTO_STORAGE_KEY, JSON.stringify({
      autoDrops: autoDrops.value,
      stopOnProfit: stopOnProfit.value,
      stopOnLoss: stopOnLoss.value,
      onWinAction: onWinAction.value,
      onWinPercent: onWinPercent.value,
      onLossAction: onLossAction.value,
      onLossPercent: onLossPercent.value,
    }))
  } catch { /* ignore */ }
}

function loadAutoSettings() {
  try {
    const saved = sessionStorage.getItem(AUTO_STORAGE_KEY)
    if (!saved) return
    const s = JSON.parse(saved) as Record<string, unknown>
    if (typeof s.autoDrops === 'number') autoDrops.value = s.autoDrops
    if (s.stopOnProfit !== undefined) stopOnProfit.value = s.stopOnProfit as number | null
    if (s.stopOnLoss !== undefined) stopOnLoss.value = s.stopOnLoss as number | null
    if (s.onWinAction === 'reset' || s.onWinAction === 'increase') onWinAction.value = s.onWinAction
    if (typeof s.onWinPercent === 'number') onWinPercent.value = s.onWinPercent
    if (s.onLossAction === 'reset' || s.onLossAction === 'increase') onLossAction.value = s.onLossAction
    if (typeof s.onLossPercent === 'number') onLossPercent.value = s.onLossPercent
  } catch { /* ignore */ }
}

// Persist whenever any auto-bet setting changes
watch(
  [autoDrops, stopOnProfit, stopOnLoss, onWinAction, onWinPercent, onLossAction, onLossPercent],
  saveAutoSettings
)

// Redraw board on rows/risk change (without full re-init)
function redrawBoard() {
  if (!app) return
  drawBoard()
}

// ----- Lifecycle -----
onMounted(() => {
  initPixi()
  loadAutoSettings()
  if (!walletStore.balance) {
    walletStore.fetchBalance()
  }
})

onBeforeUnmount(() => {
  autoBetAborted = true
  isAutoBet.value = false
  resizeObserver?.disconnect()
  if (app) {
    app.destroy(true, { children: true, texture: true, baseTexture: true })
    app = null
  }
})

// Redraw when rows/risk changes
watch([rows, risk], redrawBoard)
</script>

<style scoped>
.plinko-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* ---- Topbar ---- */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1A2C38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}
.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #B1BAD3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  transition: background 0.15s;
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.btn-back:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.game-title-bar { font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
.game-tag {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em;
  padding: 2px 8px; border-radius: 9999px;
  background: rgba(0,231,1,.1); color: var(--accent-primary, #00E701); border: 1px solid rgba(0,231,1,.2);
}
.game-topbar-actions { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.icon-btn {
  width: 32px; height: 32px; border-radius: 8px; background: var(--bg-tertiary, #213743);
  display: flex; align-items: center; justify-content: center; font-size: 14px;
  cursor: pointer; color: var(--text-secondary, #B1BAD3); transition: background 0.15s;
  border: none;
}
.icon-btn:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.icon-btn.active { background: rgba(0,231,1,.1); color: var(--accent-primary, #00E701); }

/* ---- Body ---- */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ---- Canvas Area ---- */
.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0D1B2A);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-direction: column;
  gap: 8px;
}
.canvas-bg-grid {
  position: absolute; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(0,231,1,.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,231,1,.025) 1px, transparent 1px);
  background-size: 40px 40px;
}
.canvas-bg-glow {
  position: absolute; pointer-events: none;
  right: -100px; top: -100px; width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(0,231,1,.05) 0%, transparent 65%);
}

.plinko-canvas-wrap {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  z-index: 1;
}

.pixi-mount {
  width: min(380px, 100%);
  flex: 1;
  max-height: 420px;
}

.pixi-mount canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* ---- Multiplier display row ---- */
.plinko-mult-row {
  display: flex;
  gap: 3px;
  flex-wrap: nowrap;
  justify-content: center;
  padding: 0 8px;
  width: min(380px, 100%);
  flex-shrink: 0;
  overflow-x: auto;
}
.mult-chip {
  flex: 1;
  min-width: 28px;
  max-width: 52px;
  text-align: center;
  padding: 5px 2px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  font-family: 'JetBrains Mono', monospace;
  white-space: nowrap;
  transition: transform 0.1s;
}
.mult-chip:hover { transform: translateY(-1px); }
.mult-chip.chip-high {
  background: rgba(0,231,1,.18);
  color: var(--accent-primary, #00E701);
  border: 1px solid rgba(0,231,1,.35);
}
.mult-chip.chip-mid {
  background: rgba(255,215,0,.14);
  color: var(--accent-gold, #FFD700);
  border: 1px solid rgba(255,215,0,.3);
}
.mult-chip.chip-low {
  background: rgba(20,117,225,.13);
  color: var(--color-info-light, #6ea8e8);
  border: 1px solid rgba(20,117,225,.25);
}

/* Result pills strip */
.plinko-results-strip {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
  padding: 0 8px;
  max-width: 400px;
  flex-shrink: 0;
}
.plinko-result-pill {
  padding: 3px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
}
.plinko-result-pill.big {
  background: rgba(0,231,1,.15); color: var(--accent-primary, #00E701); border: 1px solid rgba(0,231,1,.3);
}
.plinko-result-pill.mid {
  background: rgba(255,215,0,.15); color: var(--accent-gold, #FFD700); border: 1px solid rgba(255,215,0,.3);
}
.plinko-result-pill.low {
  background: rgba(20,117,225,.15); color: var(--accent-secondary, #1475E1); border: 1px solid rgba(20,117,225,.3);
}

/* ---- Game Sidebar ---- */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1A2C38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

/* Tabs */
.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7F8FA4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}
.game-tab.active { color: var(--accent-primary, #00E701); border-bottom-color: var(--accent-primary, #00E701); }
.game-tab:hover:not(.active) { color: var(--text-secondary, #B1BAD3); }

.game-tab-content {
  display: none;
  flex: 1;
  overflow-y: auto;
  flex-direction: column;
  padding: 14px;
}
.game-tab-content.active { display: flex; }

/* Controls */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7F8FA4);
  margin-bottom: 6px;
  display: block;
}
.ctrl-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 10px 0;
}

.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary, #7F8FA4);
  pointer-events: none;
}
.bet-input {
  background: var(--bg-primary, #0F1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 10px 10px 24px;
  color: var(--text-primary, #fff);
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: border-color 0.15s;
}
.bet-input:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.bet-input:disabled { opacity: 0.5; }

.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}
.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #B1BAD3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: background 0.15s;
  font-family: inherit;
}
.bet-short:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.bet-short:disabled { opacity: 0.4; cursor: not-allowed; }

.option-group {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.option-btn {
  flex: 1;
  padding: 8px 4px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #B1BAD3);
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.15s;
  text-align: center;
  font-family: inherit;
}
.option-btn:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.option-btn.active {
  background: rgba(0,231,1,.12);
  color: var(--accent-primary, #00E701);
  border-color: rgba(0,231,1,.3);
}
.option-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin: 14px 0;
}
.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7F8FA4);
  margin-bottom: 3px;
}
.sp-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-primary, #00E701);
}
.sp-value.gold { color: var(--accent-gold, #FFD700); }

.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00E701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0,231,1,.35);
  transform: translateY(-1px);
}
.btn-action:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-auto-stop {
  background: var(--color-danger, #ED4245);
  color: var(--color-on-dark, #fff);
}
.btn-auto-stop:hover:not(:disabled) {
  background: var(--color-danger, #c73537);
  box-shadow: 0 0 20px rgba(237,66,69,.35);
}

.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: 4px;
}
.bal-label { font-size: 11px; color: var(--text-tertiary, #7F8FA4); }
.bal-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #B1BAD3);
}
.bal-value.green { color: var(--accent-primary, #00E701); }

/* ---- Auto-bet controls ---- */
.auto-field {
  margin-bottom: 10px;
}
.auto-input {
  background: var(--bg-primary, #0F1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 9px 12px;
  color: var(--text-primary, #fff);
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  transition: border-color 0.15s;
}
.auto-input:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.auto-input:disabled { opacity: 0.5; cursor: not-allowed; }
.auto-select {
  background: var(--bg-primary, #0F1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 9px 12px;
  color: var(--text-primary, #fff);
  font-family: inherit;
  font-size: 13px;
  width: 100%;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.15s;
}
.auto-select:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.auto-select:disabled { opacity: 0.5; cursor: not-allowed; }
.auto-suffix {
  position: absolute;
  right: 10px;
  font-size: 12px;
  color: var(--text-tertiary, #7F8FA4);
  pointer-events: none;
}

/* Auto-bet progress */
.auto-progress {
  margin: 10px 0 4px;
}
.auto-progress-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
  overflow: hidden;
  margin-bottom: 6px;
}
.auto-progress-fill {
  height: 100%;
  background: var(--accent-primary, #00E701);
  border-radius: 9999px;
  transition: width 0.3s ease;
}
.auto-progress-label {
  font-size: 11px;
  color: var(--text-tertiary, #7F8FA4);
  font-family: 'JetBrains Mono', monospace;
}
.auto-net-pos { color: var(--accent-primary, #00E701); font-weight: 700; }
.auto-net-neg { color: var(--color-danger, #ED4245); font-weight: 700; }

/* Rules tab */
.rules-section { margin-bottom: 16px; }
.rules-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.rules-title-icon { font-size: 14px; }
.rules-list { list-style: none; display: flex; flex-direction: column; gap: 5px; }
.rules-item {
  font-size: 12px;
  color: var(--text-secondary, #B1BAD3);
  padding-left: 14px;
  position: relative;
  line-height: 1.55;
}
.rules-item::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: var(--accent-primary, #00E701);
  font-size: 10px;
  top: 1px;
}
.rules-note {
  font-size: 11px;
  color: var(--text-tertiary, #7F8FA4);
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 10px;
  margin-top: 10px;
  line-height: 1.6;
  border-left: 2px solid var(--accent-primary, #00E701);
}
.fair-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(20,117,225,.1);
  border: 1px solid rgba(20,117,225,.2);
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--accent-secondary, #1475E1);
  margin-top: 12px;
  cursor: pointer;
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
  .game-body {
    flex-direction: column;
  }
  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 280px;
  }
  .pixi-mount {
    max-height: 260px;
  }
}
</style>
